import React, { useState, useEffect, useContext } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsTable from "./fuelLogReportTable";
import { AccountsContext } from "context/AccountsContext";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const token = localStorage.getItem("token");
const API_BASE = "https://v3.dhundhoo.com/fuel/log";
const headers = { "x-dhundhoo-session": token };

function FuelLogReport() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteInvoice, setDeleteInvoice] = useState("");

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  const [form, setForm] = useState({
    invoiceNo: "",
    fuellingStation: "",
    fuelStationCity: "",
    vehicleNo: "",
    driverName: "",
    driverId: "",
    fuelOdometerReading: "",
    fuelClosingMeterReading: "",
    fuelQty: "",
    ratePerLitre: "",
    amount: "",
    mileage: "",
    fuelType: "",
    paymentMode: "",
    vendorCode: "",
    billStatus: "",
    costCenter: "",
    projectCode: "",
    remarks: "",
    lastOdometerReading: "",
    kmTravelledSinceLastFill: "",
    fuelEfficiency: "",
    costPerKm: "",
    createdBy: "admin_user",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const showSnackbar = (message, color = "success") => {
    setSnackbar({ open: true, message, color });
  };
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/all`, { headers });
      const cleaned = res.data.map(log => ({
        id: log.id,
        invoiceNo: log.invoiceNo || "-",
        invoiceDate: log.invoiceDate ? new Date(log.invoiceDate).toLocaleString("en-IN") : "-",
        fuellingStation: log.fuellingStation || "-",
        fuelStationCity: log.fuelStationCity || "-",
        vehicleNo: log.vehicleNo || "-",
        driverName: log.driverName || "-",
        driverId: log.driverId || "-",
        fuelOdometerReading: log.fuelOdometerReading?.toFixed(1) || "-",
        fuelClosingMeterReading: log.fuelClosingMeterReading?.toFixed(1) || "-",
        fuelQty: log.fuelQty?.toFixed(1) || "-",
        ratePerLitre: log.ratePerLitre?.toFixed(2) || "-",
        amount: log.amount?.toFixed(2) || "-",
        mileage: log.mileage?.toFixed(2) || log.fuelEfficiency?.toFixed(2) || "-",
        fuelType: log.fuelType || "-",
        paymentMode: log.paymentMode || "-",
        vendorCode: log.vendorCode || "-",
        billStatus: log.billStatus || "-",
        costCenter: log.costCenter || "-",
        projectCode: log.projectCode || "-",
        remarks: log.remarks || "-",
        lastOdometerReading: log.lastOdometerReading?.toFixed(1) || "-",
        kmTravelledSinceLastFill: log.kmTravelledSinceLastFill?.toFixed(2) || "-",
        fuelEfficiency: log.fuelEfficiency?.toFixed(2) || "-",
        costPerKm: log.costPerKm?.toFixed(2) || "-",
        createdBy: log.createdBy || "-",
        createdAt: log.createdAt ? new Date(log.createdAt).toLocaleString("en-IN") : "-",
      }));
      setLogs(cleaned);
    } catch (err) {
      showSnackbar("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fixed: openAddModal1 → openAddModal
  const openAddModal1 = () => {
    setForm({
      invoiceNo: "",
      fuellingStation: "",
      fuelStationCity: "",
      vehicleNo: "",
      driverName: "",
      driverId: "",
      fuelOdometerReading: "",
      fuelClosingMeterReading: "",
      fuelQty: "",
      ratePerLitre: "",
      amount: "",
      mileage: "",
      fuelType: "",
      paymentMode: "",
      vendorCode: "",
      billStatus: "",
      costCenter: "",
      projectCode: "",
      remarks: "",
      lastOdometerReading: "",
      kmTravelledSinceLastFill: "",
      fuelEfficiency: "",
      costPerKm: "",
      createdBy: "admin_user",
    });
    setOpenAddModal(true);
  };

  const handleAdd = async () => {
    const required = ["invoiceNo", "fuellingStation", "vehicleNo", "fuelQty"];
    for (let field of required) {
      if (!form[field]) {
        showSnackbar(`${field.replace(/([A-Z])/g, ' $1')} is required`, "error");
        return;
      }
    }

    const payload = {
      ...form,
      invoiceDate: new Date().toISOString().slice(0, 19),
    };

    try {
      await axios.post(`${API_BASE}/add`, payload, { headers });
      setOpenAddModal(false);
      showSnackbar("Entry added successfully!", "success");
      fetchLogs();
    } catch (err) {
      showSnackbar("Add failed: " + (err.response?.data?.message || err.message), "error");
    }
  };

  const openDeleteConfirm = (id, invoice) => {
    setDeleteId(id);
    setDeleteInvoice(invoice);
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/${deleteId}`, { headers });
      setOpenDeleteModal(false);
      showSnackbar("Entry deleted successfully!", "success");
      fetchLogs();
    } catch (err) {
      showSnackbar("Delete failed: " + (err.response?.data?.message || err.message), "error");
    }
  };

  const exportCSV = () => {
    if (logs.length === 0) {
      showSnackbar("No data to export", "error");
      return;
    }

    const headers = [
      "Invoice No,Invoice Date,Station,City,Vehicle,Driver,Driver ID,Odometer Start,Odometer End,Qty (L),Rate,Amount,Mileage,Fuel Type,Payment,Vendor,Bill Status,Cost Center,Project,Remarks,Last Odometer,KM Travelled,Efficiency,Cost/KM,Created By,Created At"
    ];

    const rows = logs.map(l => [
      l.invoiceNo, l.invoiceDate, l.fuellingStation, l.fuelStationCity,
      l.vehicleNo, l.driverName, l.driverId, l.fuelOdometerReading,
      l.fuelClosingMeterReading, l.fuelQty, l.ratePerLitre, l.amount,
      l.mileage, l.fuelType, l.paymentMode, l.vendorCode, l.billStatus,
      l.costCenter, l.projectCode, l.remarks, l.lastOdometerReading,
      l.kmTravelledSinceLastFill, l.fuelEfficiency, l.costPerKm,
      l.createdBy, l.createdAt
    ].join(","));

    const csv = [...headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Fuel_Logs_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();

    showSnackbar("CSV downloaded successfully!", "info");
  };

  const columns = [
    { Header: "Invoice", accessor: "invoiceNo" },
    { Header: "Date", accessor: "invoiceDate" },
    { Header: "Station", accessor: "fuellingStation" },
    { Header: "City", accessor: "fuelStationCity" },
    { Header: "Vehicle", accessor: "vehicleNo" },
    { Header: "Driver", accessor: "driverName" },
    { Header: "Driver ID", accessor: "driverId" },
    { Header: "Odo Start", accessor: "fuelOdometerReading" },
    { Header: "Odo End", accessor: "fuelClosingMeterReading" },
    { Header: "Qty", accessor: "fuelQty" },
    { Header: "Rate", accessor: "ratePerLitre" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Mileage", accessor: "mileage" },
    { Header: "Fuel", accessor: "fuelType" },
    { Header: "Payment", accessor: "paymentMode" },
    { Header: "Vendor", accessor: "vendorCode" },
    { Header: "Bill", accessor: "billStatus" },
    { Header: "Cost Center", accessor: "costCenter" },
    { Header: "Project", accessor: "projectCode" },
    { Header: "Remarks", accessor: "remarks" },
    { Header: "Last Odo", accessor: "lastOdometerReading" },
    { Header: "KM", accessor: "kmTravelledSinceLastFill" },
    { Header: "Efficiency", accessor: "fuelEfficiency" },
    { Header: "Cost/KM", accessor: "costPerKm" },
    { Header: "Created By", accessor: "createdBy" },
    { Header: "Created At", accessor: "createdAt" },
    { Header: "Action", accessor: "action", disableSortBy: true },
  ];

  const rows = logs.map(log => ({
    ...log,
    action: (
      <IconButton size="small" color="error" onClick={() => openDeleteConfirm(log.id, log.invoiceNo)}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="gradient" color="warning" startIcon={<AddIcon />} onClick={openAddModal1}>
              Add Entry
            </MDButton>
            <MDButton variant="gradient" color="secondary" onClick={exportCSV} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "CSV"}
            </MDButton>
          </MDBox>

          <MDBox>
            {loading ? (
              <MDBox display="flex" justifyContent="center" p={5}>
                <CircularProgress />
              </MDBox>
            ) : (
              <ReportsTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                showTotalEntries={true}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Add Fuel Log Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {Object.keys(form).map((key) => {
              if (key === "vehicleNo") {
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Autocomplete
                      disablePortal
                      options={things || []}
                      getOptionLabel={(option) => option.nickName || option.thingId}
                      value={things.find(t => t.thingId === form.vehicleNo) || null}
                      onChange={(event, newValue) => {
                        setForm({ ...form, vehicleNo: newValue?.thingId || "" });
                      }}
                      clearOnEscape
                      renderOption={(props, option) => (
                        <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                          <span>{option.nickName || "No Name"}</span>
                          <span style={{ color: "#666", fontSize: "0.85em" }}>{option.thingId}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vehicle"
                          placeholder="Search vehicle..."
                          fullWidth
                          size="small"
                          required
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                );
              }

              const isNumberField = ["amount", "Qty", "Reading", "km", "cost", "rate"].some(str => key.includes(str));

              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={key.replace(/([A-Z])/g, ' $1').trim()}
                    value={form[key] ?? ""} // ← Fix: null → ""
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (isNumberField) {
                        setForm({ ...form, [key]: rawValue === "" ? "" : Number(rawValue) });
                      } else {
                        setForm({ ...form, [key]: rawValue });
                      }
                    }}
                    type={isNumberField ? "number" : "text"}
                    required={["invoiceNo", "fuellingStation", "fuelQty"].includes(key)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={isNumberField ? { min: 0 } : {}}
                  />
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAddModal(false)} color="error">Cancel</MDButton>
          <MDButton onClick={handleAdd} variant="gradient" color="info">Add</MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteInvoice}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDeleteModal(false)} color="dark">Cancel</MDButton>
          <MDButton onClick={handleDelete} variant="gradient" color="error">Delete</MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        color={snackbar.color}
        icon={snackbar.color === "error" ? "warning" : snackbar.color === "info" ? "download" : "check"}
        title="Fuel Log"
        content={snackbar.message}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default FuelLogReport;